import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { USE_IN_MEMORY_CACHE } from '@ppg/core/cache-memory';
import { OCP_APIM_SUBSCRIPTION_KEY_HEADER } from '@ppg/core/constants';
import { EnvVarsNames } from '@ppg/core/enums';
import { EnvironmentService } from '@ppg/core/env-provider';
import { LoggerService } from '@ppg/core/logger';
import { StaticAssets } from '@ppg/core/models';

@Injectable({ providedIn: 'root' })
export class ConfigService {
  readonly #loggerService: LoggerService = inject(LoggerService);
  readonly #environment = inject(EnvironmentService);
  readonly #http = inject(HttpClient);

  getGlobalStaticAssets(language: string): Observable<StaticAssets | null> {
    return this.#getStaticAssets(language);
  }

  getSiteStaticAssets(siteId?: number, language?: string): Observable<StaticAssets | null> {
    if (!siteId) {
      this.#loggerService.warn(`[ConfigService][getSiteStaticAssets] Site is not specified`);
      return of(null);
    }

    return this.#getStaticAssets(language, siteId);
  }

  #getStaticAssets(language?: string, siteId?: number): Observable<StaticAssets | null> {
    const contentApiUrl = `${this.#environment.getEnvironmentVariable(EnvVarsNames.APIM_CONTENT_API_BASE_URL)}v2/`;
    const subscriptionKey = this.#environment.getEnvironmentVariable(EnvVarsNames.APIM_CONTENT_API_SUBSCRIPTION_KEY);
    const headers = new HttpHeaders().set(OCP_APIM_SUBSCRIPTION_KEY_HEADER, subscriptionKey ?? '');
    const params = this.#getHttpParams({
      ...(siteId && { siteId }),
      ...(language && { lang: language }),
    });

    const getStaticAssetsUrlWithParams = `${contentApiUrl}sites/staticassets?${params.toString()}`;
    const cacheContext = new HttpContext().set(USE_IN_MEMORY_CACHE, getStaticAssetsUrlWithParams);

    this.#loggerService.info(`[ConfigService] Fetching ${siteId} site static assets`);

    return this.#http.get<StaticAssets | null>(getStaticAssetsUrlWithParams, { context: cacheContext, headers }).pipe(
      map((assets: StaticAssets | null) => {
        return assets ?? { items: [] };
      }),
      catchError((e: HttpErrorResponse) => {
        const staticAssetsStr = siteId ? `static assets for ${siteId} site` : 'global static assets';
        const languageStr = language ? ` with ${language} language` : '';
        this.#loggerService.error(
          `[ConfigService][getStaticAssets] Error occurred while fetching ${staticAssetsStr}${languageStr}: ${e.message}.`,
        );
        return of(null);
      }),
    );
  }

  #getHttpParams(paramsObj: { [param: string]: string | number }): HttpParams {
    return new HttpParams({ fromObject: paramsObj });
  }
}
